/**
 * Care Plan Seed Data — Pre-populated care plans for demo residents.
 *
 * Called once on first load to populate localStorage with realistic clinical
 * care plan data so the system is immediately usable.
 */

import LocalStorageService from '../services/storage/LocalStorageService';
import {
  createCarePlan, createCarePlanSection, createCarePlanNeed,
  createCarePlanGoal, createCarePlanIntervention, createCarePlanEvaluation,
  CARE_PLAN_TYPE, CARE_PLAN_STATUS, SECTION_STATUS, RISK_LEVEL,
  GOAL_STATUS, GOAL_TYPE, GOAL_DOMAIN, INTERVENTION_STATUS,
  INTERVENTION_SOURCE, INTERVENTION_CATEGORY, NEED_SOURCE, NEED_PRIORITY,
  CARE_PLAN_SECTIONS, GENERATED_FROM,
} from './models/CarePlan';

const STORAGE_KEY = 'mf_care_plans_v2';
const SEED_FLAG = 'mf_care_plans_seeded_v2';

function _dt(daysAgo) {
  return new Date(Date.now() - daysAgo * 86400000).toISOString();
}

function buildSectionWithContent(number, overrides = {}) {
  const def = CARE_PLAN_SECTIONS.find(s => s.number === number) || {};
  return createCarePlanSection({
    number,
    key: def.key,
    title: def.title,
    status: overrides.status || SECTION_STATUS.REVIEWED,
    riskLevel: overrides.riskLevel || RISK_LEVEL.LOW,
    clinicalAlerts: overrides.clinicalAlerts || [],
    additionalNotes: overrides.notes || '',
    residentPreferences: overrides.preferences || '',
    needs: (overrides.needs || []).map(n => createCarePlanNeed(typeof n === 'string' ? { description: n } : n)),
    goals: (overrides.goals || []).map(g => createCarePlanGoal(typeof g === 'string' ? { goalText: g } : g)),
    interventions: (overrides.interventions || []).map(i => createCarePlanIntervention(typeof i === 'string' ? { interventionText: i } : i)),
    familyInput: overrides.familyInput || '',
  });
}

function buildResident1Plan() {
  const plan = createCarePlan({
    residentId: '1',
    type: CARE_PLAN_TYPE.COMPREHENSIVE,
    version: 3,
    status: CARE_PLAN_STATUS.ACTIVE,
    generatedFrom: GENERATED_FROM.AUTO_ASSESSMENT,
    createdAt: _dt(90),
    createdBy: 'RN J. Smith',
    signedOffBy: 'RN J. Smith',
    signedOffAt: _dt(88),
    lastReviewedAt: _dt(14),
    lastReviewedBy: 'RN J. Smith',
    reviewDueAt: new Date(Date.now() + 42 * 86400000).toISOString(),
  });

  plan.sections = [
    buildSectionWithContent(1, {
      preferences: 'Prefers to be called "Maggie". Early riser — up by 6:30am. Loves gardening and listening to ABC Radio. Afternoon rest 2-3pm. Warm milk at bedtime.',
      needs: [
        { description: 'Requires person-centred approach that respects long history as a retired school teacher', source: NEED_SOURCE.RESIDENT_REPORTED, priority: NEED_PRIORITY.MEDIUM },
        { description: 'Values independence — encourage self-care where safe to do so', source: NEED_SOURCE.CLINICAL_OBSERVATION, priority: NEED_PRIORITY.HIGH },
      ],
      goals: [
        { goalText: 'Margaret will maintain her daily routine preferences including morning gardening and afternoon rest', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.SOCIAL, status: GOAL_STATUS.IN_PROGRESS, residentVoice: 'I want to keep pottering in the garden every morning — it keeps me happy' },
        { goalText: 'Margaret will continue to make her own choices about daily activities and care where able', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.INDEPENDENCE, status: GOAL_STATUS.ACHIEVED },
      ],
      interventions: [
        { interventionText: 'Confirm preferred name "Maggie" and communication preferences on every shift handover', category: INTERVENTION_CATEGORY.NURSING, frequency: 'Every shift', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Review My Life Story with family input quarterly', category: INTERVENTION_CATEGORY.LIFESTYLE, frequency: 'Quarterly', responsibleRole: 'RN' },
      ],
      familyInput: 'Daughter Jane visits Tuesdays and Saturdays. Maggie loves photos of grandchildren — keep album on bedside table.',
    }),

    buildSectionWithContent(2, {
      notes: 'Primary: Alzheimer disease (early stage) — diagnosed 2023. Secondary: Hypertension, Osteoarthritis (bilateral knees), Type 2 Diabetes (diet controlled). Surgical: R hip replacement 2019. AN-ACC Class 7.',
      clinicalAlerts: ['ALLERGY: Penicillin (anaphylaxis)', 'ALLERGY: Shellfish (urticaria)'],
      needs: [
        { description: 'Multiple comorbidities requiring coordinated medication management and regular GP review', priority: NEED_PRIORITY.HIGH },
        { description: 'Allergy to Penicillin — anaphylaxis risk — ensure RED alert on all medication charts', priority: NEED_PRIORITY.CRITICAL },
      ],
      goals: [
        { goalText: 'All medical conditions will be managed within target parameters over the next 8 weeks', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.PHYSICAL, status: GOAL_STATUS.IN_PROGRESS },
      ],
      interventions: [
        { interventionText: 'GP review monthly — Dr. Sarah Chen, Sunshine Medical Practice', frequency: 'Monthly', responsibleRole: 'RN → GP', isHighlighted: true },
        { interventionText: 'Blood glucose monitoring fasting and post-prandial — target 4-8mmol/L', frequency: 'Daily', responsibleRole: 'RN/EN' },
      ],
    }),

    buildSectionWithContent(3, {
      preferences: 'Morning: Wake 6:30am, cup of tea (white, 1 sugar) before dressing. Prefers shower over bath, warm water. Chooses own clothes.\nDaytime: Garden 8-9am, common area for morning tea, room for reading/radio.\nMeals: Dining room, prefers window table with Betty and Joan.\nBedtime: 8:30pm, warm milk, door slightly open, nightlight on.',
      needs: [
        { description: 'Values consistent daily routine — changes can cause anxiety and confusion', priority: NEED_PRIORITY.HIGH, source: NEED_SOURCE.CLINICAL_OBSERVATION },
      ],
      goals: [
        { goalText: 'Margaret\'s preferred daily routine will be followed consistently by all care staff', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.SOCIAL, status: GOAL_STATUS.IN_PROGRESS },
      ],
      interventions: [
        { interventionText: 'Follow documented daily routine preferences — see section for morning/daytime/evening specifics', frequency: 'Every shift', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Allow extra time for self-care activities — do not rush Margaret', frequency: 'Every personal care', responsibleRole: 'PCA' },
      ],
    }),

    buildSectionWithContent(4, {
      riskLevel: RISK_LEVEL.MEDIUM,
      notes: 'Current mobility: Supervised walking with wheeled frame. Transfer: Stand-pivot with 1 assist. Sitting balance: Good. Standing tolerance: 5 minutes. Physiotherapy: Weekly — gait retraining and strengthening.',
      needs: [
        { description: 'Requires supervision and wheeled frame for all mobilisation due to reduced balance and bilateral knee OA', priority: NEED_PRIORITY.HIGH },
        { description: 'Requires 1-person assist for transfers using stand-pivot technique', priority: NEED_PRIORITY.HIGH },
      ],
      goals: [
        { goalText: 'Margaret will maintain current mobility level (supervised walking with wheeled frame) for the next 8 weeks', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.PHYSICAL, status: GOAL_STATUS.IN_PROGRESS, measurable: 'Walking distance maintained at minimum 20m per walk, 2x daily' },
        { goalText: 'Margaret will have no falls with injury in the next 8 weeks', goalType: GOAL_TYPE.PREVENTION, domain: GOAL_DOMAIN.SAFETY, status: GOAL_STATUS.IN_PROGRESS },
      ],
      interventions: [
        { interventionText: 'Supervised walking with wheeled frame x2 daily — minimum 20m per walk', frequency: 'Daily x2', responsibleRole: 'PCA', isHighlighted: true, source: INTERVENTION_SOURCE.INTERVENTION_LIBRARY },
        { interventionText: 'Stand-pivot transfer with 1 assist — ensure brakes applied before transfer', frequency: 'Every transfer', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Physiotherapy: gait retraining and lower limb strengthening', frequency: 'Weekly', responsibleRole: 'Physiotherapist' },
        { interventionText: 'Seated exercises program (as per physio) — encourage daily', frequency: 'Daily', responsibleRole: 'PCA' },
      ],
    }),

    buildSectionWithContent(5, {
      riskLevel: RISK_LEVEL.HIGH,
      clinicalAlerts: ['HIGH FALLS RISK'],
      notes: 'Falls risk score: 18/25 (High). Risk factors: history of 2 falls in past 6 months, postural hypotension, cognitive impairment (early Alzheimer), polypharmacy (7 medications), bilateral knee OA, impaired balance. Post-fall protocol: on anticoagulant — check for bleeding/bruising even for witnessed non-head-striking falls.',
      needs: [
        { description: 'At high risk of falls due to unsteady gait, postural hypotension, cognitive impairment, and polypharmacy', priority: NEED_PRIORITY.HIGH },
        { description: '2 falls in past 6 months (1 mechanical, 1 postural) — no injuries sustained', priority: NEED_PRIORITY.HIGH },
      ],
      goals: [
        { goalText: 'Margaret will have no falls with injury in the next 8 weeks', goalType: GOAL_TYPE.PREVENTION, domain: GOAL_DOMAIN.SAFETY, status: GOAL_STATUS.IN_PROGRESS, measurable: 'Zero falls with injury recorded in incident module' },
        { goalText: 'Falls risk factors will be actively managed and reviewed at each 8-weekly cycle', goalType: GOAL_TYPE.PREVENTION, domain: GOAL_DOMAIN.SAFETY, status: GOAL_STATUS.IN_PROGRESS },
      ],
      interventions: [
        { interventionText: 'Non-slip footwear when mobilising', frequency: 'Every shift', responsibleRole: 'PCA', isHighlighted: true, source: INTERVENTION_SOURCE.INTERVENTION_LIBRARY },
        { interventionText: 'Call bell within reach at all times', frequency: 'Every shift', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Bed at lowest height when in bed', frequency: 'Every shift', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Sensor mat beside bed — connected and functional', frequency: 'Ongoing', responsibleRole: 'RN', isHighlighted: true },
        { interventionText: 'Supervision when walking at all times', frequency: 'Every mobilisation', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Hourly rounding — visual check and offer assistance', frequency: 'Hourly', responsibleRole: 'PCA' },
        { interventionText: 'Postural hypotension management — sit on edge of bed 2 min before standing', frequency: 'Every transfer', responsibleRole: 'PCA' },
        { interventionText: 'Hip protector fitted — wear during waking hours', frequency: 'Daily', responsibleRole: 'PCA' },
        { interventionText: 'Medication review for falls-risk medications — GP to review antihypertensives', frequency: 'Within 7 days', responsibleRole: 'RN → GP' },
      ],
    }),

    buildSectionWithContent(6, {
      riskLevel: RISK_LEVEL.MEDIUM,
      notes: 'Braden score: 16 (Mild risk). Skin inspection: intact, dry skin on shins. No current wounds. Pressure-relieving mattress in situ.',
      needs: [
        { description: 'Mild risk of pressure injury due to reduced mobility and age-related skin fragility', priority: NEED_PRIORITY.MEDIUM },
      ],
      goals: [
        { goalText: 'Margaret will maintain skin integrity with no new pressure injuries for the next 8 weeks', goalType: GOAL_TYPE.PREVENTION, domain: GOAL_DOMAIN.PHYSICAL, status: GOAL_STATUS.IN_PROGRESS },
      ],
      interventions: [
        { interventionText: 'Skin inspection with every personal care episode — report any changes to RN', frequency: 'Every shift', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Apply moisturiser to dry skin areas (shins, arms) daily', frequency: 'Daily', responsibleRole: 'PCA' },
        { interventionText: 'Reposition every 2-3 hours when in bed', frequency: 'q2-3h', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Pressure-relieving mattress maintained in situ', frequency: 'Ongoing', responsibleRole: 'RN' },
      ],
    }),

    buildSectionWithContent(7, {
      riskLevel: RISK_LEVEL.MEDIUM,
      clinicalAlerts: ['On blood thinner (Apixaban) — watch for bruising/bleeding', 'Psychotropic: Risperidone 0.5mg nocte — review due 15 May'],
      notes: 'Total medications: 7 (polypharmacy flagged). High-risk: Apixaban 5mg BD (anticoagulant), Risperidone 0.5mg nocte (antipsychotic — for agitation with early Alzheimer, indication documented). PRN: Paracetamol 1g 4-6 hourly for OA pain. Last RMMR: 15 Feb 2026. Next due: Aug 2026.',
      needs: [
        { description: 'Polypharmacy (7 medications) requiring regular pharmacist review and falls-risk medication assessment', priority: NEED_PRIORITY.HIGH },
        { description: 'On anticoagulant (Apixaban) — increased bleeding risk, especially post-fall', priority: NEED_PRIORITY.HIGH },
        { description: 'Psychotropic medication (Risperidone) requires 3-monthly review per NQIP requirements', priority: NEED_PRIORITY.HIGH },
      ],
      goals: [
        { goalText: 'Margaret\'s medications will be managed safely with no medication errors and a pharmacist review by August 2026', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.SAFETY, status: GOAL_STATUS.IN_PROGRESS },
        { goalText: 'Psychotropic medication dose reduction trial will be discussed with GP by 15 May 2026', goalType: GOAL_TYPE.IMPROVEMENT, domain: GOAL_DOMAIN.SAFETY, status: GOAL_STATUS.NOT_STARTED },
      ],
      interventions: [
        { interventionText: 'Medication administered as prescribed — monitor for adverse effects each administration', frequency: 'Per prescription', responsibleRole: 'RN/EN', isHighlighted: true },
        { interventionText: 'PRN Paracetamol 1g: offer when grimacing, rubbing knees, or verbalising pain. Reassess 60 min post-dose.', frequency: 'PRN', responsibleRole: 'RN/EN', isHighlighted: true },
        { interventionText: 'Observe for signs of bleeding/bruising (Apixaban) — report immediately to RN', frequency: 'Every shift', responsibleRole: 'PCA' },
        { interventionText: 'Psychotropic review with GP — document behaviour monitoring and discuss dose reduction trial', frequency: '3-monthly', responsibleRole: 'RN → GP' },
        { interventionText: 'Pharmacist medication review (RMMR) — next due August 2026', frequency: 'Annually', responsibleRole: 'RN → Pharmacist' },
      ],
    }),

    buildSectionWithContent(8, {
      notes: 'MNA score: 11 (At risk). Weight: 62.3kg (stable — no unplanned loss). BMI: 23.1. Diet: Soft (IDDSI Level 6). Fluids: Regular (IDDSI Level 0). No food allergies. Dairy intolerance — lactose-free alternatives. Fluid target: 1500ml/day. Mealtime assist: Setup + supervision. Upright 30 min after meals.',
      preferences: 'Tea (white, 1 sugar) with breakfast. Comfort food: scones with jam. Dislikes: fish. Prefers dining room, window table with Betty and Joan.',
      needs: [
        { description: 'At nutritional risk (MNA 11) — requires weekly weight monitoring and fortified meals', priority: NEED_PRIORITY.MEDIUM },
        { description: 'Soft diet (IDDSI Level 6) due to poor dentition and mild dysphagia', priority: NEED_PRIORITY.HIGH },
      ],
      goals: [
        { goalText: 'Margaret will maintain stable weight (within 5% of 62.3kg) over the next 8 weeks', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.NUTRITION, status: GOAL_STATUS.IN_PROGRESS, measurable: 'Weekly weights — no >5% loss in 30 days' },
        { goalText: 'Margaret will maintain adequate oral intake with fortified meals and encouraged fluids', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.NUTRITION, status: GOAL_STATUS.IN_PROGRESS },
      ],
      interventions: [
        { interventionText: 'Weekly weight recording every Monday — report >5% change to RN immediately', frequency: 'Weekly', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Soft diet (IDDSI Level 6), regular fluids (Level 0) — synced to kitchen', frequency: 'Every meal', responsibleRole: 'Kitchen/PCA', isHighlighted: true },
        { interventionText: 'Lactose-free alternatives for all dairy — check menu daily', frequency: 'Every meal', responsibleRole: 'Kitchen' },
        { interventionText: 'Setup + supervision at mealtimes — encourage intake, note amounts', frequency: 'Every meal', responsibleRole: 'PCA' },
        { interventionText: 'Remain upright 30 minutes after meals — aspiration prevention', frequency: 'Every meal', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Encourage fluid intake — offer drinks hourly — target 1500ml/day', frequency: 'Hourly', responsibleRole: 'PCA' },
        { interventionText: 'Oral nutritional supplement (Ensure Plus) with afternoon tea', frequency: 'Daily', responsibleRole: 'PCA' },
      ],
    }),

    buildSectionWithContent(9, {
      preferences: 'Anglican — attends chapel service Sundays when well. Values Christmas and Easter celebrations. No specific cultural care needs beyond standard respectful care.',
      needs: [
        { description: 'Spiritual connection through weekly chapel service and pastoral visits', priority: NEED_PRIORITY.MEDIUM, source: NEED_SOURCE.RESIDENT_REPORTED },
      ],
      goals: [
        { goalText: 'Margaret will attend chapel service weekly (when well) and receive pastoral visit monthly', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.SPIRITUAL, status: GOAL_STATUS.IN_PROGRESS },
      ],
      interventions: [
        { interventionText: 'Facilitate attendance at Sunday chapel service — remind and assist with transport', frequency: 'Weekly', responsibleRole: 'PCA' },
        { interventionText: 'Pastoral care visit arranged monthly — Rev. Michael Davidson', frequency: 'Monthly', responsibleRole: 'Activities' },
      ],
    }),

    buildSectionWithContent(10, {
      notes: 'Hearing: impaired bilateral (LEFT hearing aid — battery change Wednesdays). Vision: corrected with glasses (reading + distance). Communication: speaks clearly when hearing aid in; allow extra time for processing due to early Alzheimer.',
      needs: [
        { description: 'Hearing impairment requiring hearing aid and face-to-face communication approach', priority: NEED_PRIORITY.HIGH },
        { description: 'Cognitive processing time needed due to early Alzheimer — allow extra time for responses', priority: NEED_PRIORITY.MEDIUM },
      ],
      goals: [
        { goalText: 'All staff will use documented communication strategies when interacting with Margaret', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.SOCIAL, status: GOAL_STATUS.IN_PROGRESS },
      ],
      interventions: [
        { interventionText: 'Face Margaret when speaking — speak clearly — minimise background noise', frequency: 'Every interaction', responsibleRole: 'All staff', isHighlighted: true },
        { interventionText: 'Ensure LEFT hearing aid is in place during waking hours — battery change Wednesdays', frequency: 'Daily', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Allow extra time for responses — do not rush or repeat too quickly', frequency: 'Every interaction', responsibleRole: 'All staff' },
        { interventionText: 'Call her "Maggie" not "Mrs Wilson" or "Margaret" — she responds better', frequency: 'Every interaction', responsibleRole: 'All staff', isHighlighted: true },
      ],
    }),

    buildSectionWithContent(11, {
      riskLevel: RISK_LEVEL.MEDIUM,
      notes: 'Dx: Alzheimer disease — early stage. MMSE: 22/30 (mild impairment). No wandering risk currently. Occasional sundowning 4-6pm — increased confusion and agitation. Decision-making capacity: supported — daughter Jane is nominated support person. No restrictive practices in place.',
      needs: [
        { description: 'Early Alzheimer with mild cognitive impairment (MMSE 22/30) — requires consistent routine and cognitive stimulation', priority: NEED_PRIORITY.HIGH },
        { description: 'Sundowning episodes 4-6pm — requires de-escalation strategies and calm environment', priority: NEED_PRIORITY.MEDIUM },
      ],
      goals: [
        { goalText: 'Margaret\'s cognitive function will be maintained at current level (MMSE ≥20) through stimulation activities and consistent routine', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.COGNITIVE, status: GOAL_STATUS.IN_PROGRESS },
        { goalText: 'Sundowning episodes will be managed with non-pharmacological strategies first', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.COGNITIVE, status: GOAL_STATUS.IN_PROGRESS },
      ],
      interventions: [
        { interventionText: 'Maintain consistent daily routine — minimise changes to environment or schedule', frequency: 'Ongoing', responsibleRole: 'All staff', isHighlighted: true },
        { interventionText: 'Cognitive stimulation: puzzles, reminiscence, gardening, music — daily', frequency: 'Daily', responsibleRole: 'PCA/Activities' },
        { interventionText: 'Sundowning management (4-6pm): reduce stimulation, offer warm drink, familiar music (ABC Radio), gentle redirection', frequency: 'Daily 4-6pm', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Cognitive assessment (MMSE) — repeat 6-monthly or if noticeable decline', frequency: '6-monthly', responsibleRole: 'RN' },
      ],
    }),

    buildSectionWithContent(12, {
      notes: 'GDS score: 5/15 (mildly depressed). Good social engagement at mealtimes and garden group. Occasional low mood related to grief (husband died 2024). Well-connected — daughter visits 2x/week.',
      needs: [
        { description: 'Mild depressive symptoms (GDS 5) — ongoing grief management and social engagement support', priority: NEED_PRIORITY.MEDIUM },
      ],
      goals: [
        { goalText: 'Margaret will maintain current social engagement level and report satisfaction with quality of life', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.EMOTIONAL, status: GOAL_STATUS.IN_PROGRESS },
      ],
      interventions: [
        { interventionText: 'Monitor mood daily — document changes in progress notes', frequency: 'Daily', responsibleRole: 'PCA/RN' },
        { interventionText: 'Encourage social engagement at mealtimes and garden group', frequency: 'Daily', responsibleRole: 'PCA/Activities' },
        { interventionText: 'Pastoral/counselling support available PRN — refer if low mood persists >1 week', frequency: 'As needed', responsibleRole: 'RN' },
      ],
    }),

    buildSectionWithContent(13, {
      notes: 'Chronic pain from bilateral knee OA. NRS: 3-4/10 at rest, 5-6/10 with mobilisation. Managed with regular Paracetamol + PRN. Non-pharma: warm wheat pack to knees, gentle exercise.',
      needs: [
        { description: 'Chronic pain from bilateral knee OA requiring multi-modal management', priority: NEED_PRIORITY.HIGH },
      ],
      goals: [
        { goalText: 'Margaret\'s pain will be maintained at ≤4/10 (NRS) at rest through combined pharmacological and non-pharmacological management', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.PAIN, status: GOAL_STATUS.IN_PROGRESS, measurable: 'Pain score ≤4 at rest on twice-daily assessments' },
      ],
      interventions: [
        { interventionText: 'Pain assessment (NRS) twice daily and PRN — document in progress notes', frequency: 'Twice daily', responsibleRole: 'RN/EN', isHighlighted: true },
        { interventionText: 'PRN Paracetamol 1g: offer before physiotherapy and when pain >4/10. Reassess 60 min post-dose.', frequency: 'PRN', responsibleRole: 'RN/EN', isHighlighted: true },
        { interventionText: 'Warm wheat pack to knees for 15-20 min when pain reported or before mobilisation', frequency: 'As needed', responsibleRole: 'PCA' },
        { interventionText: 'Positioning: elevate legs when seated, supportive cushion for chair', frequency: 'Ongoing', responsibleRole: 'PCA' },
      ],
    }),

    buildSectionWithContent(14, {
      notes: 'Bladder: stress incontinence (mild) — managed with pull-up pad. Bowel: regular (alternate days), no aperients needed. No catheter.',
      needs: [
        { description: 'Mild stress urinary incontinence requiring continence product and toileting schedule', priority: NEED_PRIORITY.MEDIUM },
      ],
      goals: [
        { goalText: 'Margaret\'s continence will be managed with dignity using individualised toileting schedule and appropriate products', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.CONTINENCE, status: GOAL_STATUS.IN_PROGRESS },
      ],
      interventions: [
        { interventionText: 'Prompted toileting every 2 hours during waking hours', frequency: '2-hourly', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Pull-up pad (large) — check and change as needed with personal care', frequency: 'Every personal care', responsibleRole: 'PCA' },
        { interventionText: 'Barrier cream application after each incontinence episode', frequency: 'As needed', responsibleRole: 'PCA' },
        { interventionText: 'Dignity: always close bathroom door, allow privacy where safe', frequency: 'Every toileting', responsibleRole: 'PCA' },
      ],
    }),

    buildSectionWithContent(15, {
      notes: 'Dentures: upper full, lower partial. Fit: good. Oral hygiene: needs prompting and setup assist. 6-monthly dental review — next due July 2026.',
      needs: [
        { description: 'Requires assistance with oral hygiene (prompting and setup) and denture care', priority: NEED_PRIORITY.LOW },
      ],
      goals: [
        { goalText: 'Margaret will maintain good oral health with twice-daily hygiene and 6-monthly dental reviews', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.PHYSICAL, status: GOAL_STATUS.IN_PROGRESS },
      ],
      interventions: [
        { interventionText: 'Oral hygiene assist: brush teeth/clean dentures morning and evening', frequency: 'Twice daily', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Dentures removed and soaked overnight — stored in labelled container', frequency: 'Nightly', responsibleRole: 'PCA' },
        { interventionText: 'Dental review — next due July 2026', frequency: '6-monthly', responsibleRole: 'RN → Dentist' },
      ],
    }),

    buildSectionWithContent(16, {
      notes: 'LEFT hearing aid (Phonak). Battery change: Wednesdays. Stored in bedside drawer overnight. Distance glasses: stored on nightstand.',
      interventions: [
        { interventionText: 'Hearing aid (LEFT) — check in place each morning, battery change Wednesdays, clean weekly', frequency: 'Daily', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Distance glasses — ensure clean and available, stored safely at night', frequency: 'Daily', responsibleRole: 'PCA' },
      ],
    }),

    buildSectionWithContent(17, {
      notes: 'ACD: On file (reviewed Jan 2026). Resuscitation status: NFR (Not for Resuscitation). Substitute decision-maker: Jane Wilson (daughter). Palliative pathway: NOT activated. Preferred place of death: This facility.',
      clinicalAlerts: ['NFR (Not for Resuscitation)'],
      interventions: [
        { interventionText: 'ACD and resuscitation status documented and visible in all care plan views', frequency: 'On admission + every review', responsibleRole: 'RN', isHighlighted: true },
        { interventionText: 'Review ACD and resuscitation status with family at each 8-weekly care plan review', frequency: '8-weekly', responsibleRole: 'RN' },
      ],
    }),

    buildSectionWithContent(18, {
      preferences: 'Bedtime 8:30pm. Warm milk. Door slightly open. Nightlight on. Prefers minimal disturbance during night checks.',
      notes: 'Generally sleeps well. Occasional restlessness during sundowning period (4-6pm). Sleep aide not required.',
      needs: [
        { description: 'Requires consistent bedtime routine for quality sleep', priority: NEED_PRIORITY.LOW },
      ],
      goals: [
        { goalText: 'Margaret will maintain adequate sleep quality without pharmacological intervention', goalType: GOAL_TYPE.MAINTENANCE, domain: GOAL_DOMAIN.PHYSICAL, status: GOAL_STATUS.ACHIEVED },
      ],
      interventions: [
        { interventionText: 'Follow bedtime routine: warm milk at 8:15pm, settled in bed by 8:30pm', frequency: 'Nightly', responsibleRole: 'PCA', isHighlighted: true },
        { interventionText: 'Minimise disturbance during night checks — use nightlight, do not wake unless necessary', frequency: 'Night shift', responsibleRole: 'PCA' },
        { interventionText: 'Door slightly open, nightlight on, room temperature 21-23°C', frequency: 'Nightly', responsibleRole: 'PCA' },
      ],
    }),
  ];

  return plan;
}

function buildResident2Plan() {
  const plan = createCarePlan({
    residentId: '2',
    type: CARE_PLAN_TYPE.COMPREHENSIVE,
    version: 2,
    status: CARE_PLAN_STATUS.ACTIVE,
    generatedFrom: GENERATED_FROM.AUTO_ASSESSMENT,
    createdAt: _dt(60),
    createdBy: 'RN A. Martinez',
    signedOffBy: 'RN A. Martinez',
    signedOffAt: _dt(58),
    lastReviewedAt: _dt(7),
    lastReviewedBy: 'RN A. Martinez',
    reviewDueAt: new Date(Date.now() + 49 * 86400000).toISOString(),
  });

  plan.sections = CARE_PLAN_SECTIONS.map(def => {
    const overrides = {
      1: { preferences: 'Prefers "George". Retired mechanic. Loves cricket on TV and woodwork.', needs: ['Values independence and maintaining daily structure'], goals: [{ goalText: 'George will maintain his preferred daily routine and social connections', status: GOAL_STATUS.IN_PROGRESS, domain: GOAL_DOMAIN.SOCIAL }], interventions: ['Encourage independence with daily activities — standby assist only'] },
      2: { notes: 'Primary: COPD (moderate). Secondary: Atrial fibrillation, Chronic kidney disease stage 3. AN-ACC Class 5.', needs: ['Multiple chronic conditions requiring coordinated respiratory and cardiac management'], goals: [{ goalText: 'Respiratory function will be maintained with current COPD management plan', status: GOAL_STATUS.IN_PROGRESS }], interventions: ['Monitor SpO2 daily — target >92%', 'Chest physiotherapy and breathing exercises daily'] },
      4: { riskLevel: RISK_LEVEL.LOW, needs: ['Independently mobile with walking stick — monitor for fatigue'], goals: [{ goalText: 'George will maintain independent mobility with walking stick', status: GOAL_STATUS.ACHIEVED }], interventions: ['Encourage independent mobilisation — walking stick to remain accessible'] },
      5: { riskLevel: RISK_LEVEL.MEDIUM, clinicalAlerts: ['MODERATE FALLS RISK'], needs: ['Falls risk due to COPD-related breathlessness and AF medication'], goals: [{ goalText: 'George will have no falls in the next 8 weeks', status: GOAL_STATUS.IN_PROGRESS }], interventions: ['Call bell within reach', 'Non-slip footwear', 'Rest breaks during mobilisation if breathless'] },
      7: { riskLevel: RISK_LEVEL.MEDIUM, clinicalAlerts: ['On Warfarin — INR monitoring required'], needs: ['Warfarin therapy requiring regular INR monitoring', 'Multiple inhalers — check technique monthly'], goals: [{ goalText: 'INR will remain within therapeutic range (2.0-3.0)', status: GOAL_STATUS.IN_PROGRESS }], interventions: ['INR testing weekly — currently stable in range', 'Inhaler technique check monthly by RN'] },
      8: { needs: ['Good appetite — standard diet, no modifications required'], goals: [{ goalText: 'George will maintain stable weight and adequate nutrition', status: GOAL_STATUS.ACHIEVED }], interventions: ['Monthly weight recording', 'Encourage adequate fluid intake — 1500ml/day'] },
    };

    const o = overrides[def.number] || {};
    return buildSectionWithContent(def.number, o);
  });

  return plan;
}

function buildResident3InterimPlan() {
  const plan = createCarePlan({
    residentId: '3',
    type: CARE_PLAN_TYPE.INTERIM,
    version: 1,
    status: CARE_PLAN_STATUS.ACTIVE,
    createdAt: _dt(1),
    createdBy: 'RN K. Patel',
    signedOffBy: 'RN K. Patel',
    signedOffAt: _dt(1),
    reviewDueAt: new Date(Date.now() + 1 * 86400000).toISOString(),
  });

  const sec2 = plan.sections.find(s => s.sectionNumber === 2);
  if (sec2) { sec2.clinicalAlerts = ['ALLERGY: Sulfa drugs']; sec2.additionalNotes = 'New admission — awaiting full assessment. Dx: Parkinson disease, Depression.'; }
  const sec4 = plan.sections.find(s => s.sectionNumber === 4);
  if (sec4) { sec4.additionalNotes = 'Wheeled walker. Requires 1-assist for transfers.'; sec4.riskLevel = RISK_LEVEL.HIGH; }
  const sec5 = plan.sections.find(s => s.sectionNumber === 5);
  if (sec5) { sec5.riskLevel = RISK_LEVEL.HIGH; sec5.clinicalAlerts = ['HIGH FALLS RISK — NEW ADMISSION']; }
  const sec7 = plan.sections.find(s => s.sectionNumber === 7);
  if (sec7) { sec7.additionalNotes = 'Medications: Levodopa/Carbidopa, Sertraline, Metoprolol, Paracetamol PRN. Webster pack from Valley Pharmacy.'; }
  const sec8 = plan.sections.find(s => s.sectionNumber === 8);
  if (sec8) { sec8.additionalNotes = 'Regular diet. Needs setup assist and supervision. Mild tremor affects self-feeding.'; }
  const sec17 = plan.sections.find(s => s.sectionNumber === 17);
  if (sec17) { sec17.clinicalAlerts = ['FOR RESUSCITATION']; sec17.additionalNotes = 'Resuscitation status: For Resuscitation. ACD: Not yet completed.'; }

  return plan;
}

export function seedCarePlans() {
  if (LocalStorageService.get(SEED_FLAG, false)) {
    return false;
  }

  const existing = LocalStorageService.get(STORAGE_KEY, []);
  const plans = [
    buildResident1Plan(),
    buildResident2Plan(),
    buildResident3InterimPlan(),
  ];

  LocalStorageService.set(STORAGE_KEY, [...existing, ...plans]);
  LocalStorageService.set(SEED_FLAG, true);

  if (process.env.NODE_ENV === 'development') {
    console.debug('[CarePlanSeed] Seeded', plans.length, 'care plans for', plans.map(p => `Resident ${p.residentId} (${p.type})`).join(', ')); // eslint-disable-line no-console
  }
  return true;
}

export function resetCarePlanSeed() {
  LocalStorageService.set(SEED_FLAG, false);
  LocalStorageService.set(STORAGE_KEY, []);
}

export default seedCarePlans;
